/**
 * EdenDate translation layer — Aliyun MT via our mail service.
 * - config (source / target / autoSend / autoRecv) persisted in localStorage
 * - translate(text) with an LRU-ish cache
 * - auto-translation of incoming bubbles through a MutationObserver
 */
import {getEdenLang} from '@/edenLang';

export type EdenTrConfig = {
  from: string;      // 'auto' or a code
  to: string;        // target code
  autoSend: boolean; // translate our outgoing text before sending
  autoRecv: boolean; // translate incoming bubbles
};

const KEY = 'eden_tr';

export const TR_LANGS = [
  {code: 'auto', label: '自动检测'},
  {code: 'zh', label: '简体中文'},
  {code: 'en', label: 'English'},
  {code: 'es', label: 'Español'},
  {code: 'hi', label: 'हिन्दी'},
  {code: 'ar', label: 'العربية'},
  {code: 'pt', label: 'Português'},
  {code: 'ru', label: 'Русский'},
  {code: 'id', label: 'Indonesia'},
  {code: 'fr', label: 'Français'},
  {code: 'de', label: 'Deutsch'},
  {code: 'ja', label: '日本語'},
  {code: 'ko', label: '한국어'},
  {code: 'tr', label: 'Türkçe'},
  {code: 'vi', label: 'Tiếng Việt'},
  {code: 'th', label: 'ไทย'},
  {code: 'ur', label: 'اردو'},
  {code: 'ms', label: 'Melayu'},
  {code: 'it', label: 'Italiano'}
];

const API = 'https://mail.edendate.infchat.infinite.boo/api/translate';
const cache = new Map<string, string>();

export function getTrConfig(): EdenTrConfig {
  try {
    const raw = localStorage.getItem(KEY);
    if(raw) {
      const parsed = JSON.parse(raw);
      return {
        from: parsed.from || 'auto',
        to: parsed.to || getEdenLang() || 'en',
        autoSend: !!parsed.autoSend,
        autoRecv: !!parsed.autoRecv
      };
    }
  } catch(e) {}
  return {from: 'auto', to: getEdenLang() || 'en', autoSend: false, autoRecv: false};
}

export function setTrConfig(patch: Partial<EdenTrConfig>) {
  const next = Object.assign(getTrConfig(), patch);
  try { localStorage.setItem(KEY, JSON.stringify(next)); } catch(e) {}
  if(patch.autoRecv !== undefined) {
    if(next.autoRecv) startAutoTranslate(); else stopAutoTranslate();
  }
  return next;
}

export function trLangLabel(code: string) {
  const l = TR_LANGS.find((x) => x.code === code);
  return l ? l.label : code;
}

export async function translateText(text: string, to?: string, from?: string): Promise<string> {
  const cfg = getTrConfig();
  const target = to || cfg.to;
  const source = from || cfg.from;
  if(!text || !text.trim()) return '';
  if(target && source && source !== 'auto' && target === source) return text;

  const cacheKey = `${source}|${target}|${text}`;
  if(cache.has(cacheKey)) return cache.get(cacheKey);

  const res = await fetch(API, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({text, from: source, to: target})
  });
  const data = await res.json();
  if(!data || !data.ok) throw new Error(data && data.error || 'translate failed');

  const out = data.text || '';
  if(cache.size > 500) cache.clear();
  cache.set(cacheKey, out);
  return out;
}

export function openTrPicker(title: string, options: {code: string; label: string}[], current: string, onPick: (code: string) => void) {
  const existing = document.querySelector('.eden-lang-sheet');
  if(existing) existing.remove();

  const wrap = document.createElement('div');
  wrap.className = 'eden-lang-sheet';
  const card = document.createElement('div');
  card.className = 'eden-lang-sheet-card';

  const head = document.createElement('div');
  head.className = 'eden-lang-sheet-title';
  head.textContent = title;
  card.appendChild(head);

  options.forEach((o) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'eden-lang-sheet-item' + (o.code === current ? ' on' : '');
    btn.innerHTML = `<span>${o.label}</span>`;
    btn.addEventListener('click', () => { wrap.remove(); onPick(o.code); });
    card.appendChild(btn);
  });

  wrap.appendChild(card);
  wrap.addEventListener('click', (e) => { if(e.target === wrap) wrap.remove(); });
  document.body.appendChild(wrap);
}

// ------------------------------------------------------------ auto receive
let observer: MutationObserver | null = null;

function translationNode(original: HTMLElement, text: string) {
  const el = document.createElement('div');
  el.className = 'eden-auto-translation';
  el.textContent = text;
  el.dataset.edenTr = '1';
  return el;
}

async function translateBubble(bubble: HTMLElement) {
  if(!bubble || bubble.dataset.edenTrDone === '1') return;
  const textEl = bubble.querySelector('.message, .text-content, .translatable-message') as HTMLElement;
  if(!textEl) return;

  const text = (textEl.innerText || '').trim();
  if(!text || text.length < 2) return;
  if(bubble.querySelector('.eden-auto-translation')) {
    bubble.dataset.edenTrDone = '1';
    return;
  }

  bubble.dataset.edenTrDone = '1';
  try {
    const out = await translateText(text);
    if(!out || out === text) return;
    const node = translationNode(textEl, out);
    textEl.appendChild(document.createElement('br'));
    textEl.appendChild(node);
    textEl.classList.add('eden-has-translation');
  } catch(e) {
    delete bubble.dataset.edenTrDone;
  }
}

function scan(root: ParentNode) {
  const cfg = getTrConfig();
  if(!cfg.autoRecv) return;
  // only incoming bubbles have .is-out === false / lack .own
  root.querySelectorAll('.bubble').forEach((b) => {
    const el = b as HTMLElement;
    if(el.classList.contains('is-out') || el.closest('.is-out')) return;
    translateBubble(el);
  });
}

export function startAutoTranslate() {
  if(observer) return;
  scan(document);
  observer = new MutationObserver((mutations) => {
    for(const m of mutations) {
      m.addedNodes.forEach((n) => {
        if(n.nodeType === 1) scan(n as HTMLElement);
      });
    }
  });
  observer.observe(document.body, {childList: true, subtree: true});
}

export function stopAutoTranslate() {
  if(observer) {
    observer.disconnect();
    observer = null;
  }
  document.querySelectorAll('.eden-auto-translation').forEach((e) => e.remove());
  document.querySelectorAll('[data-eden-tr-done]').forEach((e) => {
    (e as HTMLElement).removeAttribute('data-eden-tr-done');
  });
}
